/**
 * hydrationVerifier.js — RC 1.53 Phase 4.2.1
 *
 * Verifies the terminal payload from a CV variant dispatch and, when
 * it is missing or stale, performs an authoritative READ-ONLY refetch
 * from the backend BEFORE finalize-charge is invoked.
 *
 * Hard rails (operator-locked, 2026-05-11 PM):
 *   • READ-ONLY. NO POST. NO LLM dispatch. NO `/api/runs` call.
 *   • Does NOT trigger a new charge. The finalize-charge call sits
 *     downstream of this verifier in the section.
 *   • If hydration fails (backend can't find a complete row), the
 *     verifier raises a `recoverable=true` error so the section
 *     surfaces the same calm "still generating in the background"
 *     toast as the existing 120 s slow-path fallback.
 *
 * Why this exists:
 *   The polling helper returns a terminal envelope when the run row
 *   reaches `run_status='complete'`. In rare cases (e.g. operator
 *   tab-switch mid-async, network flap during the final poll, a
 *   stale envelope from an in-flight `replay_200` race) the
 *   `variant.generated_text` field can be empty / missing even
 *   though the row exists. Phase 4.2.1's lifecycle contract says:
 *   re-read the authoritative row, do NOT regenerate, do NOT
 *   re-dispatch the LLM. The verifier centralises this rule.
 */

import { trace } from './actionTrace.js';

const ACTION = 'cv_variant';

/**
 * verifyAndHydrateCvVariant(variant, { apiGet }) → Promise<variant>
 *
 * Returns the original variant when it passes verification.
 * Returns a hydrated variant (refetched from /api/cv-variants/{id})
 * when the terminal payload is stale/incomplete and the row is
 * complete server-side.
 *
 * Throws a `recoverable=true` error when even the authoritative
 * refetch returns an incomplete row. The section catches this and
 * surfaces the existing "still generating in the background" toast.
 */
export async function verifyAndHydrateCvVariant(variant, { apiGet }) {
  if (!variant || typeof variant !== 'object') {
    const err = new Error('Verifier: terminal payload is missing.');
    err.code = 'cv_variant_terminal_payload_missing';
    err.recoverable = true;
    throw err;
  }

  const variantId = variant.variant_id || variant.id;
  if (!variantId) {
    const err = new Error('Verifier: variant_id missing from terminal payload.');
    err.code = 'cv_variant_id_missing';
    err.recoverable = false;  // genuine bug, not recoverable
    throw err;
  }

  // Quick happy-path check. The polling helper has already done the
  // heavy lifting; in 99% of cases this short-circuits.
  const looksFresh = (
    typeof variant.generated_text === 'string'
    && variant.generated_text.length > 0
  );
  if (looksFresh) {
    trace(ACTION, 'verify_fresh_payload_ok', { variantId, contentLen: variant.generated_text.length });
    return variant;
  }

  // Stale / incomplete payload — refetch authoritative row.
  trace(ACTION, 'verify_stale_payload_refetching', { variantId });
  let row;
  try {
    row = await apiGet(`/api/cv-variants/${variantId}`);
  } catch (e) {
    trace(ACTION, 'verify_refetch_failed', { variantId, error: e?.message });
    const err = new Error('Verifier: could not refetch CV variant from backend.');
    err.code = 'cv_variant_refetch_failed';
    err.cause = e;
    err.recoverable = true;
    throw err;
  }

  if (!row || typeof row !== 'object') {
    const err = new Error('Verifier: refetched row missing.');
    err.code = 'cv_variant_refetch_empty';
    err.recoverable = true;
    throw err;
  }

  // The backend returns the canonical row shape with `run_status` and
  // `content_markdown`. Map it to the same envelope shape the section
  // consumes (`variant_id`, `generated_text`, etc.) so downstream
  // code (finalize-charge, setGeneratedResult) is unchanged.
  const runStatus = row.run_status || (row.content_markdown ? 'complete' : 'pending');
  const content   = row.content_markdown || row.generated_text || '';

  if (runStatus !== 'complete' || !content) {
    trace(ACTION, 'verify_authoritative_still_incomplete', {
      variantId, runStatus, contentLen: content.length,
    });
    const err = new Error('Verifier: authoritative row is still incomplete.');
    err.code = 'cv_variant_authoritative_incomplete';
    err.recoverable = true;
    throw err;
  }

  const hydrated = {
    variant_id:     row.id || variantId,
    variant_name:   row.variant_name || variant.variant_name,
    role_family:    row.role_family || variant.role_family,
    fit_summary:    row.content_json || variant.fit_summary || {},
    generated_text: content,
    created_at:     row.created_at || variant.created_at,
    run_status:     'complete',
    // Preserve any extra fields from the original envelope so we do
    // not lose billing-relevant metadata (idempotent_replay, etc.).
    ...variant,
    // ... but force the hydrated fields to win over the stale envelope.
    generated_text: content,
    variant_id:     row.id || variantId,
  };
  trace(ACTION, 'verify_hydrated_from_authoritative_row', {
    variantId, contentLen: content.length,
  });
  return hydrated;
}
